import { createContext, useContext, useState, useCallback } from "react";

const LanguageContext = createContext(null);

const translations = {
  fr: {
    // Nav & Layout
    dashboard: "Tableau de bord",
    upload_video: "Importer une vidéo",
    knowledge_base: "Base de connaissances",
    ask_knoria: "Demander à Knoria",
    quiz: "Quiz",
    onboarding: "Intégration",
    leaderboard: "Classement",
    review_queue: "File de révision",
    analytics: "Analytique",
    dictionary: "Dictionnaire",
    partners: "Partenaires",
    admin: "Admin",
    settings: "Paramètres",
    collapse: "Réduire",
    sign_out: "Déconnexion",
    categories: "Catégories",
    certifications: "Certifications",
    amf_compliance: "Conformité AMF",
    scoring_amf: "Scoring AMF",
    firewall_ai: "Firewall IA",
    tech_stack: "Tech Stack",
    enterprise: "Entreprise",
    video_queue: "File d'attente",
    team_analytics: "Équipe",
    platform_knos: "Platform KNORIA",
    overview: "Vue d'ensemble",
    audit_logs: "Logs d'Audit",
    feature_flags: "Feature Flags",

    // Login / Register
    welcome_back: "Bienvenue",
    sign_in: "Connectez-vous",
    sign_in_subtitle: "Accédez à votre espace de formation",
    email_address: "Adresse courriel",
    email_placeholder: "vous@entreprise.com",
    password: "Mot de passe",
    login_btn: "Connexion",
    no_account: "Pas de compte?",
    request_access: "Demander accès",
    register_title: "Créer un compte",
    register_subtitle: "Rejoignez votre équipe sur KNORIA",
    full_name: "Nom complet",
    partner_code: "Code partenaire",
    partner_code_placeholder: "Entrez votre code (optionnel)",
    create_account: "Créer mon compte",
    already_account: "Déjà un compte?",
    sign_in_link: "Se connecter",
    password_requirements: "8 caractères min., 1 majuscule, 1 chiffre",
    invite_token: "Code d'invitation",

    // Dashboard
    welcome: "Bienvenue",
    videos_watched: "Vidéos vues",
    quizzes_done: "Quiz complétés",
    questions_asked: "Questions posées",
    favorites: "Favoris",
    level: "Niv.",
    badges: "Badges",
    see_all: "Voir tout",
    ai_gap_detector: "Ta progression",
    ai_gap_subtitle: "Trois indicateurs clairs pour situer ton apprentissage",
    video_coverage: "Vidéos vues",
    knowledge_engagement: "Interactions",
    quiz_mastery: "Quiz réussis",
    unexplored_types: "Types non explorés",
    // Dashboard — Ta prochaine action (P0 iter — Eric 2026-07-24)
    next_action_title: "Ta prochaine action",
    next_action_watch_lead: "Nouvelle vidéo à découvrir",
    next_action_watch_sub: "Prends 5 minutes pour la regarder",
    next_action_quiz_lead: "Consolide tes acquis",
    next_action_quiz_sub: "Un quiz rapide pour renforcer tes connaissances",
    next_action_explore_lead: "Ajoute ton premier favori",
    next_action_explore_sub: "Retrouve rapidement les contenus qui comptent",
    next_action_done_lead: "Bravo, tu es à jour !",
    next_action_done_sub: "Explore la base de connaissances pour approfondir",
    next_action_btn_watch: "Regarder maintenant",
    next_action_btn_quiz: "Faire un quiz",
    next_action_btn_explore: "Explorer",
    unexplored_hint_prefix: "Type non exploré :",
    unexplored_hint_cta: "Commence maintenant",
    recommended_for_you: "Recommandé pour vous",
    recommended_subtitle: "Basé sur votre activité et les tendances",
    not_watched: "Pas encore vue",
    popular: "Populaire",
    views: "vues",
    trending_questions: "Questions tendance",
    total_videos: "Total Videos",
    knowledge_units: "Connaissances",
    approved: "approuvées",
    pending_review: "En révision",
    pending: "En attente",
    problems: "Problèmes",
    unresolved: "Non résolus",
    recent_videos: "Vidéos récentes",
    see_all_link: "Tout voir",
    no_video: "Aucune vidéo",
    import_first: "Importez votre première vidéo de formation",
    import_btn: "Importer",
    my_favorites: "Mes Favoris",
    no_favorites: "Aucun favori",
    add_favorites_hint: "Ajoutez des connaissances ou videos",
    recent_problems: "Problèmes récents",
    in_processing: "en traitement",
    relaunch: "Relancer",
    ai_score: "Score IA",
    import_video: "Importer une vidéo",
    points: "points",

    // AI Coach
    ai_coach: "Coach IA Personnel",
    ai_coach_subtitle: "Conseils personnalisés basés sur votre activité",
    loading_coach: "Analyse de votre profil...",

    // Search
    search_placeholder: "Rechercher videos, connaissances, transcripts...",
    no_results: "Aucun résultat pour",
    results: "resultat(s)",
    videos: "Videos",
    knowledge: "Connaissances",
    transcripts: "Transcripts",

    // Upload
    upload_title: "Importer une vidéo",
    upload_subtitle: "Vidéo ou audio — KNORIA extrait automatiquement les connaissances, Q&R et procédures.",
    click_to_upload: "Cliquez pour choisir",
    or_drag_drop: "ou glissez-déposez",
    upload_formats: "Video (MP4, MOV, AVI, WebM) ou Audio (MP3, WAV, M4A) — max 500 Mo",
    title_label: "Titre",
    description_label: "Description",
    description_placeholder: "Brève description du contenu...",
    training_theme: "Thème de formation",
    general_training: "Formation générale",
    sales_training: "Ventes & Objections",
    procedures_training: "Procédures & Processus",
    compliance_training: "Conformité & Réglementation",
    product_training: "Connaissance produit",
    onboarding_training: "Intégration (Onboarding)",
    what_happens_next: "Que se passe-t-il après?",
    step_upload: "Envoi du fichier",
    step_audio: "Extraction audio",
    step_transcription: "Transcription Whisper",
    step_extraction: "Extraction IA (GPT-5.2)",
    step_validation: "Validation qualité",
    upload_and_process: "Envoyer & traiter",
    uploading: "Envoi en cours...",
    file_received: "Fichier envoyé !",
    pipeline_processing: "Le pipeline KNORIA traite votre fichier en arrière-plan.",
    pipeline_timing: "Transcription → Extraction IA → Validation — cela peut prendre quelques minutes.",
    back_to_dashboard: "Retour au tableau de bord",
    view_video: "Voir la vidéo",
    file_too_small: "Fichier trop petit",
    format_not_supported: "Format non supporte",
    select_file: "Sélectionnez un fichier",
    enter_title: "Entrez un titre",

    // Video Player
    processing: "Traitement en cours",
    pipeline_analyzing: "Le pipeline d'IA analyse votre vidéo...",
    failed_processing: "Échec du traitement",
    unknown_error: "Erreur inconnue",
    video_not_available: "Video non disponible",
    open_video: "Ouvrir la video",
    retry: "Reessayer",
    space_play: "Play/Pause",
    fullscreen: "Plein écran",
    mute: "Muet",
    transcript: "Transcript",
    search_transcript: "Rechercher dans le transcript...",
    no_transcript: "Aucun transcript",
    no_knowledge_extracted: "Aucune connaissance extraite",
    extraction_in_progress: "Extraction en cours...",
    transcription_in_progress: "Transcription en cours...",
    description: "Description",
    share_video: "Partager",
    reprocess: "Retraiter",
    download_video: "Télécharger",

    // Knowledge
    all_types: "Tous les types",
    export_pdf: "Exporter PDF",
    no_knowledge: "Aucune connaissance trouvee",
    search_knowledge: "Rechercher dans la base...",
    try_different_search: "Essayez une autre recherche",
    upload_to_build: "Importez des vidéos pour bâtir votre base de connaissances",
    all_types: "Tous les types",
    qa_type: "FAQ",
    procedures_type: "Procédures",
    scripts_type: "Scripts de vente",
    objections_type: "Objections",
    rebuttals_type: "Réfutations",
    concepts_type: "Concepts",
    syntheses_type: "Synthèses",
    steps: "Étapes",
    scenario: "Scénario",
    response: "Réponse",
    definition: "Définition",
    key_points: "Points clés",
    confidence: "confiance",
    discussion: "Discussion",
    add_comment: "Ajouter un commentaire...",
    send: "Envoyer",
    no_comments: "Aucun commentaire. Soyez le premier !",
    untitled: "Sans titre",

    // Quiz
    generate_quiz: "Générer un Quiz",
    questions: "questions",
    start_quiz: "Commencer",
    submit_quiz: "Soumettre",
    next_question: "Question suivante",
    quiz_results: "Résultats du Quiz",
    correct: "Correct",
    incorrect: "Incorrect",
    score: "Score",
    time_remaining: "Temps restant",
    exam_mode: "Mode Examen",
    exam_mode_desc: "Chronomètre activé, pas de retour en arrière",
    quiz_history: "Historique des quiz",
    no_quiz_history: "Aucun quiz complété",
    average_score: "Score moyen",
    best_score: "Meilleur score",
    all_themes: "Tous les sujets",
    select_theme: "Choisir un sujet",
    num_questions: "Nombre de questions",
    not_enough_knowledge: "Pas assez de connaissances pour générer un quiz",
    quiz_excellent: "Excellent ! Vous maitrisez bien ce sujet.",
    quiz_good: "Bon effort ! Revoyez les points faibles.",
    quiz_review: "Il faut réviser ! Consultez les sources ci-dessous.",

    // Chat
    ask_question: "Posez votre question...",
    thinking: "Knoria réfléchit...",
    related_questions: "Questions reliées",
    sources: "Sources",
    chat_title: "Demander à Knoria",
    chat_subtitle: "Posez vos questions sur la base de connaissances",
    no_chat_history: "Aucune conversation",
    start_asking: "Commencez par poser une question",

    // Leaderboard
    leaderboard_title: "Classement & Progression",
    leaderboard_subtitle: "Suivez votre progression et comparez-vous à l'équipe",
    my_profile: "Mon profil",
    team_ranking: "Classement de l'equipe",
    you: "vous",
    no_leaderboard: "Aucune donnée de classement encore",
    earn_points_hint: "Regardez des videos et completez des quiz pour gagner des points !",
    pts: "pts",

    // Certifications
    certifications_title: "Certifications",
    certifications_subtitle: "Vos diplomes et certifications obtenues",
    no_certifications: "Aucune certification obtenue",
    complete_pathways: "Complétez des parcours d'intégration pour obtenir des certifications",
    download_certificate: "Télécharger",
    certified_on: "Certifie le",

    // Settings
    settings_title: "Paramètres",
    change_password: "Changer le mot de passe",
    current_password: "Mot de passe actuel",
    new_password: "Nouveau mot de passe",
    confirm_password: "Confirmer le nouveau mot de passe",
    update_password: "Mettre à jour le mot de passe",
    password_updated: "Mot de passe mis à jour avec succès",
    language: "Langue",
    appearance: "Apparence",
    dark_mode: "Mode sombre",
    light_mode: "Mode clair",
    profile: "Profil",
    account_info: "Informations du compte",

    // Billing / Enterprise
    billing_title: "Entreprise",
    no_organization: "Aucune organisation",
    no_org_subtitle: "Vous n'êtes pas encore associé à une entreprise.",
    choose_plan: "Choisir un plan",
    switch_org: "Changer",
    founder_unlimited: "Fondateur — Illimite",
    active: "Actif",
    payment_pending: "En attente de paiement",
    activate_plan: "Activez votre plan",
    activate_plan_desc: "Completez le paiement pour activer toutes les fonctionnalites",
    pay_now: "Payer maintenant",
    members: "Membres",
    plan: "Plan",
    partner_code_label: "Code partenaire",
    invite_member: "Inviter un nouveau membre",
    invite_email: "Adresse courriel",
    invite_email_placeholder: "prenom.nom@entreprise.com",
    org_role: "Role dans l'organisation",
    role_employee: "Employe",
    role_employee_desc: "Accès formation, quiz, chat",
    role_admin: "Administrateur",
    role_admin_desc: "Gestion videos et connaissances",
    role_vp: "Vice-President",
    role_vp_desc: "Analytique equipe + admin",
    role_ceo: "CEO / Dirigeant",
    role_ceo_desc: "Accès complet à tout",
    send_invitation: "Envoyer l'invitation",
    invitation_sent: "Invitation envoyée !",
    pending_invitations: "Invitations en attente",
    change_role: "Changer le role",
    remove_member: "Retirer",
    annual: "Annuel",
    monthly: "Mensuel",

    // AMF Compliance
    amf_title: "Conformité AMF",
    amf_subtitle: "Formation continue obligatoire — Suivi des heures",
    log_hours: "Enregistrer des heures",
    my_hours: "Mes heures",
    team: "Équipe",
    continuing_education: "Formation continue",
    compliant: "Conforme",
    in_progress: "En cours",
    hours_remaining: "restantes",
    activities_recorded: "Activités enregistrées",
    log_activity: "Enregistrer une activité",
    activity_title: "Titre de l'activité",
    activity_type: "Type",
    activity_hours: "Heures",
    activity_category: "Catégorie",
    video_watched: "Vidéo visionnée",
    quiz_completed: "Quiz complété",
    webinar: "Webinaire",
    course: "Cours / Formation",
    reading: "Lecture",
    cat_conformity: "Conformité",
    cat_products: "Produits financiers",
    cat_ethics: "Éthique & Déontologie",
    cat_technical: "Technique",
    cat_general: "Général",
    team_compliance: "Conformité de l'équipe",
    compliant_count: "conformes",

    // Compliance Scoring
    scoring_title: "Scoring Conformité AMF",
    scoring_subtitle: "Analyse automatique des vidéos pour détecter les risques",
    analyze: "Analyser",
    analyzing: "Analyse IA...",
    conformant: "Conforme",
    attention: "Attention",
    non_conformant: "Non conforme",
    risks_detected: "Risques détectés",
    recommendations: "Recommandations",
    no_videos_to_analyze: "Aucune vidéo terminée à analyser",

    // Platform Dashboard
    platform_dashboard: "Tableau de bord Plateforme",
    platform_realtime: "Vue d'ensemble en temps réel",
    users: "Utilisateurs",
    this_week: "cette semaine",
    organizations: "Organisations",
    active_companies: "Entreprises actives",
    knowledge_extracted: "Éléments extraits",
    ai_questions: "Questions IA",
    rag_queries: "Requêtes RAG",
    quizzes_completed: "Quiz complétés",
    ai_videos: "Vidéos IA",
    activity_7days: "Activité (7 derniers jours)",
    connections: "Connexions",
    video_pipeline: "Pipeline Vidéo",
    completed: "Terminées",
    in_progress_status: "En cours",
    failed: "Échouées",
    total_videos_label: "Vidéos totales",
    recent_alerts: "Alertes récentes",
    view_all: "Voir tout",
    no_critical_alert: "Aucune alerte critique",
    security_compliance: "Sécurité & Conformité",
    audit_events_recorded: "événements d'audit enregistrés",
    active_flags: "Flags actifs",
    total_revenue: "Revenus totaux",
    view_logs: "Voir les logs",

    // Audit Logs
    audit_title: "Logs d'Audit",
    events_recorded: "événements enregistrés",
    refresh: "Rafraichir",
    filter_by_action: "Filtrer par action (ex: auth.login, video.upload)...",
    all_severities: "Toutes severites",
    severity_info: "Info",
    severity_warning: "Attention",
    severity_critical: "Critique",
    lines: "lignes",
    load_more: "Charger plus",
    no_logs: "Aucun log trouvé",
    modify_filters: "Modifiez vos filtres ou attendez de nouvelles actions.",
    severity: "Severite",
    action: "Action",
    user_label: "Utilisateur",
    entity: "Entite",
    ip: "IP",
    date: "Date",

    // Feature Flags
    flags_title: "Feature Flags",
    flags_subtitle: "fonctionnalites configurables",
    flags_info: "Les feature flags permettent d'activer ou désactiver des fonctionnalités pour l'ensemble de la plateforme sans déploiement.",
    flag_active: "Actif",
    flag_inactive: "Inactif",
    last_modified: "Dernière modification",
    no_flags: "Aucun feature flag configure",

    // Pricing
    pricing_title: "Tarification",
    pricing_subtitle: "Choisissez le plan adapté à votre équipe",
    per_month: "/mois",
    per_year: "/an",
    most_popular: "Plus populaire",
    users_label: "utilisateurs",
    videos_per_month: "videos/mois",
    unlimited: "illimite",
    choose_this_plan: "Choisir ce plan",
    contact_us: "Contactez-nous",

    // Common
    loading: "Chargement...",
    error: "Erreur",
    save: "Sauvegarder",
    cancel: "Annuler",
    delete: "Supprimer",
    edit: "Modifier",
    close: "Fermer",
    complete: "Terminé",
    processing_status: "en cours",
    failed_status: "Échoué",
    back: "Retour",
    confirm: "Confirmer",
    yes: "Oui",
    no: "Non",
    or: "ou",
    of: "sur",
    no_data: "Aucune donnée",
    copied: "Copie!",
    copy: "Copier",

    // Video Detail
    processing_video: "Traitement en cours",
    pipeline_analyzing_video: "Le pipeline d'IA analyse votre vidéo...",
    failed_processing: "Échec du traitement",
    download: "Télécharger",
    share: "Partager",
    reprocess_video: "Retraiter",
    transcript_tab: "Transcript",
    knowledge_tab: "Connaissances",
    segments: "segments",
    search_transcript: "Rechercher dans le transcript...",
    no_transcript_yet: "Aucun transcript disponible",
    no_knowledge_yet: "Aucune connaissance extraite",
    copy_link: "Copier le lien",
    link_copied: "Lien copie!",
    share_link: "Lien de partage",
    expires_in: "Expire dans",
    days: "jours",
    create_share_link: "Créer un lien de partage",
    public_link: "Lien public",

    // AI Video
    ai_video_title: "Génération Vidéo IA",
    ai_video_subtitle: "Creez des videos avec un avatar IA",
    generate_video: "Générer",
    generating: "Génération en cours...",
    ai_video_history: "Historique des generations",
    avatar: "Avatar",
    voice: "Voix",
    script_text: "Texte du script",
    no_ai_videos: "Aucune vidéo IA générée",

    // Review Queue
    review_title: "File de révision",
    review_subtitle: "Validez les connaissances extraites avant publication",
    approve: "Approuver",
    reject: "Rejeter",
    approve_all: "Tout approuver",
    items_pending: "elements en attente",
    no_pending_items: "Aucun element en attente de revision",
    all_reviewed: "Toutes les connaissances ont été révisées",

    // Firewall
    firewall_title: "Firewall IA",
    firewall_subtitle: "Filtrage et contrôle des requêtes IA",
    blocked_queries: "Requetes bloquees",
    allowed_queries: "Requêtes autorisées",
    firewall_rules: "Règles de filtrage",
    add_rule: "Ajouter une règle",

    // Jargon Dictionary
    jargon_title: "Dictionnaire",
    jargon_subtitle: "Termes et définitions du domaine financier",
    add_term: "Ajouter un terme",
    term: "Terme",
    definition_label: "Définition",
    example: "Exemple",
    search_terms: "Rechercher un terme...",
    no_terms: "Aucun terme dans le dictionnaire",
    add_first_term: "Ajoutez votre premier terme",

    // Shared Video
    shared_by: "Partage par",
    expired_link: "Ce lien de partage a expiré",
    invalid_link: "Lien de partage invalide",
    powered_by: "Propulse par",

    // Onboarding
    onboarding_title: "Parcours d'intégration",
    onboarding_subtitle: "Suivez votre progression d'intégration",
    start_pathway: "Commencer",
    continue_pathway: "Continuer",
    completed_pathway: "Terminé",
    step_of: "Étape",
    progress_label: "Progression",

    // Team Analytics
    team_title: "Analytique d'equipe",
    team_subtitle: "Performance et progression de l'equipe",
    member: "Membre",
    role: "Role",
    videos_watched_col: "Vidéos vues",
    quizzes_done_col: "Quiz faits",
    avg_score: "Score moyen",
    last_active: "Dernière activité",
    no_team_data: "Aucune donnée d'equipe",

    // Analytics
    analytics_title: "Analytique",
    analytics_subtitle: "Statistiques et metriques de la plateforme",
    total_users: "Total utilisateurs",
    active_users: "Utilisateurs actifs",
    total_queries: "Total requêtes",
    total_knowledge: "Total connaissances",
  },

  en: {
    // Nav & Layout
    dashboard: "Dashboard",
    upload_video: "Upload Video",
    knowledge_base: "Knowledge Base",
    ask_knoria: "Ask Knoria",
    quiz: "Quiz",
    onboarding: "Onboarding",
    leaderboard: "Leaderboard",
    review_queue: "Review Queue",
    analytics: "Analytics",
    dictionary: "Dictionary",
    partners: "Partners",
    admin: "Admin",
    settings: "Settings",
    collapse: "Collapse",
    sign_out: "Sign out",
    categories: "Categories",
    certifications: "Certifications",
    amf_compliance: "AMF Compliance",
    scoring_amf: "AMF Scoring",
    firewall_ai: "AI Firewall",
    tech_stack: "Tech Stack",
    enterprise: "Enterprise",
    video_queue: "Video Queue",
    team_analytics: "Team",
    platform_knos: "KNORIA Platform",
    overview: "Overview",
    audit_logs: "Audit Logs",
    feature_flags: "Feature Flags",

    // Login / Register
    welcome_back: "Welcome",
    sign_in: "Sign in to your account",
    sign_in_subtitle: "Access your training workspace",
    email_address: "Email address",
    email_placeholder: "you@company.com",
    password: "Password",
    login_btn: "Sign In",
    no_account: "Don't have an account?",
    request_access: "Request access",
    register_title: "Create account",
    register_subtitle: "Join your team on KNORIA",
    full_name: "Full name",
    partner_code: "Partner code",
    partner_code_placeholder: "Enter your code (optional)",
    create_account: "Create my account",
    already_account: "Already have an account?",
    sign_in_link: "Sign in",
    password_requirements: "Min 8 chars, 1 uppercase, 1 digit",
    invite_token: "Invitation code",

    // Dashboard
    welcome: "Welcome",
    videos_watched: "Videos watched",
    quizzes_done: "Quizzes done",
    questions_asked: "Questions asked",
    favorites: "Favorites",
    level: "Lv.",
    badges: "Badges",
    see_all: "See all",
    ai_gap_detector: "Your progress",
    ai_gap_subtitle: "Three clear indicators to gauge your learning",
    video_coverage: "Videos watched",
    knowledge_engagement: "Interactions",
    quiz_mastery: "Quizzes passed",
    unexplored_types: "Unexplored types",
    // Dashboard — Next action
    next_action_title: "Your next action",
    next_action_watch_lead: "New video to discover",
    next_action_watch_sub: "Take 5 minutes to watch it",
    next_action_quiz_lead: "Reinforce your knowledge",
    next_action_quiz_sub: "A quick quiz to strengthen what you learned",
    next_action_explore_lead: "Add your first favorite",
    next_action_explore_sub: "Quickly find the content that matters",
    next_action_done_lead: "Great, you're up to date!",
    next_action_done_sub: "Explore the knowledge base to go deeper",
    next_action_btn_watch: "Watch now",
    next_action_btn_quiz: "Take a quiz",
    next_action_btn_explore: "Explore",
    unexplored_hint_prefix: "Unexplored type:",
    unexplored_hint_cta: "Start now",
    recommended_for_you: "Recommended for you",
    recommended_subtitle: "Based on your activity and trends",
    not_watched: "Not yet watched",
    popular: "Popular",
    views: "views",
    trending_questions: "Trending questions",
    total_videos: "Total Videos",
    knowledge_units: "Knowledge Units",
    approved: "approved",
    pending_review: "Pending review",
    pending: "Pending",
    problems: "Problems",
    unresolved: "Unresolved",
    recent_videos: "Recent videos",
    see_all_link: "See all",
    no_video: "No videos",
    import_first: "Upload your first training video",
    import_btn: "Upload",
    my_favorites: "My Favorites",
    no_favorites: "No favorites",
    add_favorites_hint: "Add knowledge or videos to favorites",
    recent_problems: "Recent problems",
    in_processing: "processing",
    relaunch: "Retry",
    ai_score: "AI Score",
    import_video: "Upload a video",
    points: "points",

    // AI Coach
    ai_coach: "Personal AI Coach",
    ai_coach_subtitle: "Personalized advice based on your activity",
    loading_coach: "Analyzing your profile...",

    // Search
    search_placeholder: "Search videos, knowledge, transcripts...",
    no_results: "No results for",
    results: "result(s)",
    videos: "Videos",
    knowledge: "Knowledge",
    transcripts: "Transcripts",

    // Upload
    upload_title: "Upload a video",
    upload_subtitle: "Video or audio — KNORIA automatically extracts knowledge, Q&A and procedures.",
    click_to_upload: "Click to upload",
    or_drag_drop: "or drag and drop",
    upload_formats: "Video (MP4, MOV, AVI, WebM) or Audio (MP3, WAV, M4A) — max 500 MB",
    title_label: "Title",
    description_label: "Description",
    description_placeholder: "Brief description of the content...",
    training_theme: "Training theme",
    general_training: "General Training",
    sales_training: "Sales & Objections",
    procedures_training: "Procedures & Processes",
    compliance_training: "Compliance & Regulations",
    product_training: "Product Knowledge",
    onboarding_training: "Onboarding",
    what_happens_next: "What happens next?",
    step_upload: "File upload",
    step_audio: "Audio extraction",
    step_transcription: "Whisper transcription",
    step_extraction: "AI extraction (GPT-5.2)",
    step_validation: "Quality validation",
    upload_and_process: "Upload & Process",
    uploading: "Uploading...",
    file_received: "File uploaded!",
    pipeline_processing: "The KNORIA pipeline is processing your file in the background.",
    pipeline_timing: "Transcription → AI Extraction → Validation — this may take a few minutes.",
    back_to_dashboard: "Back to dashboard",
    view_video: "View video",
    file_too_small: "File too small",
    format_not_supported: "Format not supported",
    select_file: "Select a file",
    enter_title: "Enter a title",

    // Video Player
    processing: "Processing",
    pipeline_analyzing: "AI pipeline is analyzing your video...",
    failed_processing: "Processing failed",
    unknown_error: "Unknown error",
    video_not_available: "Video not available",
    open_video: "Open video",
    retry: "Retry",
    space_play: "Play/Pause",
    fullscreen: "Fullscreen",
    mute: "Mute",
    transcript: "Transcript",
    search_transcript: "Search transcript...",
    no_transcript: "No transcript",
    no_knowledge_extracted: "No knowledge extracted",
    extraction_in_progress: "Extraction in progress...",
    transcription_in_progress: "Transcription in progress...",
    description: "Description",
    share_video: "Share",
    reprocess: "Reprocess",
    download_video: "Download",

    // Knowledge
    all_types: "All types",
    export_pdf: "Export PDF",
    no_knowledge: "No knowledge found",
    search_knowledge: "Search knowledge base...",
    try_different_search: "Try adjusting your search query",
    upload_to_build: "Upload videos to start building your knowledge base",
    all_types: "All Types",
    qa_type: "FAQ",
    procedures_type: "Procedures",
    scripts_type: "Sales Scripts",
    objections_type: "Objections",
    rebuttals_type: "Rebuttals",
    concepts_type: "Concepts",
    syntheses_type: "Syntheses",
    question: "Question",
    answer: "Answer",
    steps: "Steps",
    scenario: "Scenario",
    response: "Response",
    definition: "Definition",
    key_points: "Key points",
    confidence: "confidence",
    discussion: "Discussion",
    add_comment: "Add a comment...",
    send: "Send",
    no_comments: "No comments yet. Be the first!",
    untitled: "Untitled",

    // Quiz
    generate_quiz: "Generate Quiz",
    questions: "questions",
    start_quiz: "Start",
    submit_quiz: "Submit",
    next_question: "Next question",
    quiz_results: "Quiz Results",
    correct: "Correct",
    incorrect: "Incorrect",
    score: "Score",
    time_remaining: "Time remaining",
    exam_mode: "Exam Mode",
    exam_mode_desc: "Timer enabled, no going back",
    quiz_history: "Quiz History",
    no_quiz_history: "No quizzes completed",
    average_score: "Average score",
    best_score: "Best score",
    all_themes: "All topics",
    select_theme: "Select a topic",
    num_questions: "Number of questions",
    not_enough_knowledge: "Not enough knowledge to generate a quiz",
    quiz_excellent: "Excellent! You have a strong grasp of this topic.",
    quiz_good: "Good effort! Review the weak points.",
    quiz_review: "Time to review! Check the sources below.",

    // Chat
    ask_question: "Ask your question...",
    thinking: "KNORIA is thinking...",
    related_questions: "Related questions",
    sources: "Sources",
    chat_title: "Ask Knoria",
    chat_subtitle: "Ask questions about the knowledge base",
    no_chat_history: "No conversations",
    start_asking: "Start by asking a question",

    // Leaderboard
    leaderboard_title: "Leaderboard & Progress",
    leaderboard_subtitle: "Track your progress and compare with the team",
    my_profile: "My profile",
    team_ranking: "Team ranking",
    you: "you",
    no_leaderboard: "No leaderboard data yet",
    earn_points_hint: "Watch videos and complete quizzes to earn points!",
    pts: "pts",

    // Certifications
    certifications_title: "Certifications",
    certifications_subtitle: "Your earned diplomas and certifications",
    no_certifications: "No certifications earned",
    complete_pathways: "Complete onboarding pathways to earn certifications",
    download_certificate: "Download",
    certified_on: "Certified on",

    // Settings
    settings_title: "Settings",
    change_password: "Change password",
    current_password: "Current password",
    new_password: "New password",
    confirm_password: "Confirm new password",
    update_password: "Update password",
    password_updated: "Password updated successfully",
    language: "Language",
    appearance: "Appearance",
    dark_mode: "Dark mode",
    light_mode: "Light mode",
    profile: "Profile",
    account_info: "Account information",

    // Billing / Enterprise
    billing_title: "Enterprise",
    no_organization: "No organization",
    no_org_subtitle: "You are not yet associated with a company.",
    choose_plan: "Choose a plan",
    switch_org: "Switch",
    founder_unlimited: "Founder — Unlimited",
    active: "Active",
    payment_pending: "Payment pending",
    activate_plan: "Activate your plan",
    activate_plan_desc: "Complete payment to activate all features",
    pay_now: "Pay now",
    members: "Members",
    plan: "Plan",
    partner_code_label: "Partner code",
    invite_member: "Invite a new member",
    invite_email: "Email address",
    invite_email_placeholder: "first.last@company.com",
    org_role: "Organization role",
    role_employee: "Employee",
    role_employee_desc: "Training access, quiz, chat",
    role_admin: "Administrator",
    role_admin_desc: "Video and knowledge management",
    role_vp: "Vice-President",
    role_vp_desc: "Team analytics + admin",
    role_ceo: "CEO / Director",
    role_ceo_desc: "Full access to everything",
    send_invitation: "Send invitation",
    invitation_sent: "Invitation sent!",
    pending_invitations: "Pending invitations",
    change_role: "Change role",
    remove_member: "Remove",
    annual: "Annual",
    monthly: "Monthly",

    // AMF Compliance
    amf_title: "AMF Compliance",
    amf_subtitle: "Mandatory continuing education — Hours tracking",
    log_hours: "Log hours",
    my_hours: "My hours",
    team: "Team",
    continuing_education: "Continuing education",
    compliant: "Compliant",
    in_progress: "In progress",
    hours_remaining: "remaining",
    activities_recorded: "Activities recorded",
    log_activity: "Log an activity",
    activity_title: "Activity title",
    activity_type: "Type",
    activity_hours: "Hours",
    activity_category: "Category",
    video_watched: "Video watched",
    quiz_completed: "Quiz completed",
    webinar: "Webinar",
    course: "Course / Training",
    reading: "Reading",
    cat_conformity: "Compliance",
    cat_products: "Financial products",
    cat_ethics: "Ethics & Deontology",
    cat_technical: "Technical",
    cat_general: "General",
    team_compliance: "Team compliance",
    compliant_count: "compliant",

    // Compliance Scoring
    scoring_title: "AMF Compliance Scoring",
    scoring_subtitle: "Automatic video analysis for risk detection",
    analyze: "Analyze",
    analyzing: "AI analyzing...",
    conformant: "Compliant",
    attention: "Attention",
    non_conformant: "Non-compliant",
    risks_detected: "Risks detected",
    recommendations: "Recommendations",
    no_videos_to_analyze: "No completed videos to analyze",

    // Platform Dashboard
    platform_dashboard: "Platform Dashboard",
    platform_realtime: "Real-time overview",
    users: "Users",
    this_week: "this week",
    organizations: "Organizations",
    active_companies: "Active companies",
    knowledge_extracted: "Extracted items",
    ai_questions: "AI Questions",
    rag_queries: "RAG Queries",
    quizzes_completed: "Quizzes completed",
    ai_videos: "AI Videos",
    activity_7days: "Activity (last 7 days)",
    connections: "Logins",
    video_pipeline: "Video Pipeline",
    completed: "Completed",
    in_progress_status: "In progress",
    failed: "Failed",
    total_videos_label: "Total videos",
    recent_alerts: "Recent alerts",
    view_all: "View all",
    no_critical_alert: "No critical alerts",
    security_compliance: "Security & Compliance",
    audit_events_recorded: "audit events recorded",
    active_flags: "Active flags",
    total_revenue: "Total revenue",
    view_logs: "View logs",

    // Audit Logs
    audit_title: "Audit Logs",
    events_recorded: "events recorded",
    refresh: "Refresh",
    filter_by_action: "Filter by action (e.g., auth.login, video.upload)...",
    all_severities: "All severities",
    severity_info: "Info",
    severity_warning: "Warning",
    severity_critical: "Critical",
    lines: "lines",
    load_more: "Load more",
    no_logs: "No logs found",
    modify_filters: "Modify your filters or wait for new actions.",
    severity: "Severity",
    action: "Action",
    user_label: "User",
    entity: "Entity",
    ip: "IP",
    date: "Date",

    // Feature Flags
    flags_title: "Feature Flags",
    flags_subtitle: "configurable features",
    flags_info: "Feature flags allow you to enable or disable features across the entire platform without deployment.",
    flag_active: "Active",
    flag_inactive: "Inactive",
    last_modified: "Last modified",
    no_flags: "No feature flags configured",

    // Pricing
    pricing_title: "Pricing",
    pricing_subtitle: "Choose the plan that fits your team",
    per_month: "/month",
    per_year: "/year",
    most_popular: "Most popular",
    users_label: "users",
    videos_per_month: "videos/month",
    unlimited: "unlimited",
    choose_this_plan: "Choose this plan",
    contact_us: "Contact us",

    // Common
    loading: "Loading...",
    error: "Error",
    save: "Save",
    cancel: "Cancel",
    delete: "Delete",
    edit: "Edit",
    close: "Close",
    complete: "complete",
    processing_status: "processing",
    failed_status: "failed",
    back: "Back",
    confirm: "Confirm",
    yes: "Yes",
    no: "No",
    or: "or",
    of: "of",
    no_data: "No data",
    copied: "Copied!",
    copy: "Copy",

    // Video Detail
    processing_video: "Processing",
    pipeline_analyzing_video: "AI pipeline is analyzing your video...",
    failed_processing: "Processing failed",
    download: "Download",
    share: "Share",
    reprocess_video: "Reprocess",
    transcript_tab: "Transcript",
    knowledge_tab: "Knowledge",
    segments: "segments",
    search_transcript: "Search transcript...",
    no_transcript_yet: "No transcript available",
    no_knowledge_yet: "No knowledge extracted",
    copy_link: "Copy link",
    link_copied: "Link copied!",
    share_link: "Share link",
    expires_in: "Expires in",
    days: "days",
    create_share_link: "Create share link",
    public_link: "Public link",

    // AI Video
    ai_video_title: "AI Video Generation",
    ai_video_subtitle: "Create videos with an AI avatar",
    generate_video: "Generate",
    generating: "Generating...",
    ai_video_history: "Generation history",
    avatar: "Avatar",
    voice: "Voice",
    script_text: "Script text",
    no_ai_videos: "No AI videos generated",

    // Review Queue
    review_title: "Review Queue",
    review_subtitle: "Validate extracted knowledge before publishing",
    approve: "Approve",
    reject: "Reject",
    approve_all: "Approve all",
    items_pending: "items pending",
    no_pending_items: "No items pending review",
    all_reviewed: "All knowledge has been reviewed",

    // Firewall
    firewall_title: "AI Firewall",
    firewall_subtitle: "AI query filtering and control",
    blocked_queries: "Blocked queries",
    allowed_queries: "Allowed queries",
    firewall_rules: "Filtering rules",
    add_rule: "Add rule",

    // Jargon Dictionary
    jargon_title: "Dictionary",
    jargon_subtitle: "Financial domain terms and definitions",
    add_term: "Add term",
    term: "Term",
    definition_label: "Definition",
    example: "Example",
    search_terms: "Search a term...",
    no_terms: "No terms in the dictionary",
    add_first_term: "Add your first term",

    // Shared Video
    shared_by: "Shared by",
    expired_link: "This share link has expired",
    invalid_link: "Invalid share link",
    powered_by: "Powered by",

    // Onboarding
    onboarding_title: "Onboarding Pathways",
    onboarding_subtitle: "Track your onboarding progress",
    start_pathway: "Start",
    continue_pathway: "Continue",
    completed_pathway: "Completed",
    step_of: "Step",
    progress_label: "Progress",

    // Team Analytics
    team_title: "Team Analytics",
    team_subtitle: "Team performance and progress",
    member: "Member",
    role: "Role",
    videos_watched_col: "Videos watched",
    quizzes_done_col: "Quizzes done",
    avg_score: "Avg score",
    last_active: "Last active",
    no_team_data: "No team data",

    // Analytics
    analytics_title: "Analytics",
    analytics_subtitle: "Platform statistics and metrics",
    total_users: "Total users",
    active_users: "Active users",
    total_queries: "Total queries",
    total_knowledge: "Total knowledge",
  }
};

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("knoria-lang") || "fr");

  const switchLang = useCallback((newLang) => {
    setLang(newLang);
    localStorage.setItem("knoria-lang", newLang);
  }, []);

  // Tâche 1329 (Eric 2026-06-30) — Appliquer la langue ENREGISTRÉE SUR LE
  // COMPTE, sans la ré-enregistrer. `switchLang` écrit dans le stockage local
  // du navigateur ; l'appeler ici ferait remonter la préférence du compte vers
  // le poste courant à chaque chargement, ce qui empêcherait justement de
  // distinguer « ce compte parle anglais » de « ce navigateur a servi à un
  // anglophone une fois ». Le stockage local est quand même mis à jour pour
  // que l'écran de CONNEXION s'ouvre dans la bonne langue au prochain
  // passage — c'est le seul écran où le compte n'est pas encore connu.
  const appliquerLangueDuCompte = useCallback((langueDuCompte) => {
    if (langueDuCompte !== "fr" && langueDuCompte !== "en") return;
    setLang(langueDuCompte);
    try {
      localStorage.setItem("knoria-lang", langueDuCompte);
    } catch { /* stockage indisponible : la session courante suffit */ }
  }, []);

  const t = useCallback((key) => {
    return translations[lang]?.[key] || translations.fr[key] || key;
  }, [lang]);

  // Tâche 1324 (Eric 2026-06-30) — Traduire sur place, sans inventer de clé.
  //
  // Le bouton « EN » ne traduisait que ce qui passe par le dictionnaire :
  // 3 243 textes écrits directement dans le JSX restaient en français quoi
  // qu'il arrive, d'où une interface anglaise à moitié française. Créer
  // 3 243 clés de dictionnaire serait un travail de scribe, et chaque clé
  // éloigne le texte de l'écran où il s'affiche (on ne sait plus ce que
  // `pathway_empty_hint_2` veut dire). `tr("Ajouter", "Add")` garde les deux
  // langues côte à côte, là où le texte vit.
  const tr = useCallback((fr, en) => (lang === "fr" ? fr : (en ?? fr)), [lang]);

  return (
    <LanguageContext.Provider value={{ lang, switchLang, appliquerLangueDuCompte, t, tr }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLang must be used within LanguageProvider");
  return context;
}
